/**
 * Ventana de filas visibles.
 *
 * Una tabla del ERP tiene que aguantar 50.000 filas sin degradarse
 * (ERP-UX.md §3), y 50.000 nodos en el DOM no aguantan. Se pintan las que
 * caben más un margen por arriba y por abajo, y el resto se simula con dos
 * huecos de la altura correspondiente.
 *
 * El cálculo se saca aquí, fuera del componente, porque los errores de
 * ventana —una fila que parpadea al llegar al final, un salto al hacer
 * scroll rápido— son de aritmética, y la aritmética se puede probar sin
 * abrir un navegador.
 */

export interface WindowInput {
  /** Filas totales. */
  totalRows: number;
  /** Alto de una fila en píxeles. Constante: la densidad es uniforme. */
  rowHeight: number;
  /** Alto visible del contenedor. */
  viewportHeight: number;
  /** Desplazamiento actual. */
  scrollTop: number;
  /** Filas de más a cada lado, para que el scroll rápido no deje huecos. */
  overscan?: number;
}

export interface RowWindow {
  /** Primera fila a pintar, incluida. */
  start: number;
  /** Última fila a pintar, excluida. */
  end: number;
  /** Relleno superior, en píxeles. */
  paddingTop: number;
  /** Relleno inferior, en píxeles. */
  paddingBottom: number;
}

const DEFAULT_OVERSCAN = 8;

export function computeWindow(input: WindowInput): RowWindow {
  const { totalRows, rowHeight, viewportHeight, scrollTop } = input;
  const overscan = input.overscan ?? DEFAULT_OVERSCAN;

  if (totalRows <= 0 || rowHeight <= 0) {
    return { start: 0, end: 0, paddingTop: 0, paddingBottom: 0 };
  }

  // Un scroll negativo existe: es el rebote elástico de macOS e iOS. Sin
  // esta pinza, `start` se iría por debajo de cero y la tabla saltaría al
  // soltar.
  const offset = Math.max(scrollTop, 0);

  const firstVisible = Math.floor(offset / rowHeight);
  const visibleCount = Math.ceil(viewportHeight / rowHeight);

  const start = Math.max(firstVisible - overscan, 0);
  // `+1` porque una fila parcialmente visible arriba y otra abajo cuentan
  // las dos: sin él, la última fila aparece cortada al final del scroll.
  const end = Math.min(firstVisible + visibleCount + overscan + 1, totalRows);

  return {
    start,
    end,
    paddingTop: start * rowHeight,
    paddingBottom: Math.max(totalRows - end, 0) * rowHeight,
  };
}

/**
 * ¿Toca pedir la página siguiente?
 *
 * Se dispara antes de tocar fondo: si se esperara al final, la persona
 * vería la tabla pararse en seco mientras llega la respuesta.
 */
export function shouldLoadMore(input: {
  scrollTop: number;
  viewportHeight: number;
  contentHeight: number;
  /** Cuántos píxeles antes del final se pide más. */
  threshold?: number;
}): boolean {
  const threshold = input.threshold ?? 400;
  const remaining = input.contentHeight - (input.scrollTop + input.viewportHeight);
  return remaining <= threshold;
}
